import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navigation from '../Navigation/Navigation';
import Footer from '../Footer/Footer';
import { deepDivePageMap } from '../../content/deepDivePages';
import './DeepDivePage.css';

const DeepDivePage = ({ slug }) => {
  const page = deepDivePageMap[slug];

  useEffect(() => {
    if (!page) {
      return undefined;
    }

    const siteUrl = 'https://leviathancodingsystems.com';
    const pageUrl = `${siteUrl}/${page.slug}`;
    const previousTitle = document.title;
    const metaDescription = document.querySelector('meta[name="description"]');
    const previousDescription = metaDescription?.getAttribute('content') || '';
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    const previousKeywords = metaKeywords?.getAttribute('content') || '';
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    const previousCanonical = canonicalLink?.getAttribute('href') || '';

    const updateMeta = (selector, value) => {
      const element = document.querySelector(selector);

      if (!element) {
        return '';
      }

      const previous = element.getAttribute('content') || '';
      element.setAttribute('content', value);
      return previous;
    };

    const previousMeta = {
      ogUrl: updateMeta('meta[property="og:url"]', pageUrl),
      twitterUrl: updateMeta('meta[name="twitter:url"]', pageUrl),
      ogTitle: updateMeta('meta[property="og:title"]', page.metaTitle),
      twitterTitle: updateMeta('meta[name="twitter:title"]', page.metaTitle),
      ogDescription: updateMeta('meta[property="og:description"]', page.metaDescription),
      twitterDescription: updateMeta('meta[name="twitter:description"]', page.metaDescription)
    };

    document.title = page.metaTitle;

    if (metaDescription) {
      metaDescription.setAttribute('content', page.metaDescription);
    }

    if (metaKeywords && page.metaKeywords) {
      metaKeywords.setAttribute('content', page.metaKeywords);
    }

    if (canonicalLink) {
      canonicalLink.setAttribute('href', pageUrl);
    }

    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: page.pageTitle,
      description: page.metaDescription,
      url: pageUrl,
      inLanguage: 'en-GB',
      isPartOf: {
        '@type': 'WebSite',
        name: 'Leviathan Coding Systems',
        url: siteUrl
      }
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: siteUrl
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: page.pageTitle,
          item: pageUrl
        }
      ]
    };

    const webPageSchemaScript = document.createElement('script');
    webPageSchemaScript.type = 'application/ld+json';
    webPageSchemaScript.setAttribute('data-schema', 'deep-dive-webpage');
    webPageSchemaScript.text = JSON.stringify(webPageSchema);
    document.head.appendChild(webPageSchemaScript);

    const breadcrumbSchemaScript = document.createElement('script');
    breadcrumbSchemaScript.type = 'application/ld+json';
    breadcrumbSchemaScript.setAttribute('data-schema', 'deep-dive-breadcrumb');
    breadcrumbSchemaScript.text = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbSchemaScript);

    return () => {
      document.title = previousTitle;

      if (metaDescription) {
        metaDescription.setAttribute('content', previousDescription);
      }

      if (metaKeywords) {
        metaKeywords.setAttribute('content', previousKeywords);
      }

      if (canonicalLink) {
        canonicalLink.setAttribute('href', previousCanonical);
      }

      if (webPageSchemaScript.parentNode) {
        webPageSchemaScript.parentNode.removeChild(webPageSchemaScript);
      }

      if (breadcrumbSchemaScript.parentNode) {
        breadcrumbSchemaScript.parentNode.removeChild(breadcrumbSchemaScript);
      }

      const restoreMeta = (selector, value) => {
        const element = document.querySelector(selector);

        if (element) {
          element.setAttribute('content', value);
        }
      };

      restoreMeta('meta[property="og:url"]', previousMeta.ogUrl);
      restoreMeta('meta[name="twitter:url"]', previousMeta.twitterUrl);
      restoreMeta('meta[property="og:title"]', previousMeta.ogTitle);
      restoreMeta('meta[name="twitter:title"]', previousMeta.twitterTitle);
      restoreMeta('meta[property="og:description"]', previousMeta.ogDescription);
      restoreMeta('meta[name="twitter:description"]', previousMeta.twitterDescription);
    };
  }, [page]);

  if (!page) {
    return null;
  }

  return (
    <>
      <div className="starfield"></div>
      <Navigation />
      <main className="deep-dive-page">
        <section className="deep-dive-hero">
          <motion.div
            className="deep-dive-shell"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="section-badge">
              <span className="badge-icon">↗</span>
              {page.badge}
            </div>

            <h1 className="deep-dive-title">{page.pageTitle}</h1>
            <p className="deep-dive-lead">{page.lead}</p>
            {page.intro && <p className="deep-dive-intro">{page.intro}</p>}

            <div className="deep-dive-actions">
              {page.secondaryCta && (
                <Link to={page.secondaryCta.to} className="btn-secondary">{page.secondaryCta.label}</Link>
              )}
              <Link to="/" className="btn-secondary">Back to Home</Link>
              <Link to="/#contact" className="btn-primary">Start a Project</Link>
            </div>
          </motion.div>
        </section>

        <section className="deep-dive-content">
          <motion.div
            className="deep-dive-shell"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <div className="deep-dive-rich-layout">
              {page.founderQuote && (
                <div className="deep-dive-quote-panel">
                  <p>{page.founderQuote}</p>
                </div>
              )}

              <div className="deep-dive-rich-grid">
                <div className="deep-dive-main-column">
                  {page.sections.map((section) => (
                    <article key={section.title} className="deep-dive-article">
                      <h2>{section.title}</h2>
                      {section.paragraphs.map((paragraph) => (
                        <p key={paragraph}>{paragraph}</p>
                      ))}
                      {Array.isArray(section.points) && section.points.length > 0 && (
                        <ul className="deep-dive-inline-list">
                          {section.points.map((point) => (
                            <li key={point}>{point}</li>
                          ))}
                        </ul>
                      )}
                      {Array.isArray(section.links) && section.links.length > 0 && (
                        <p className="deep-dive-section-links">
                          {section.links.map((link, index) => (
                            <React.Fragment key={link.to}>
                              {index > 0 ? ' · ' : ''}
                              <Link to={link.to}>{link.label}</Link>
                            </React.Fragment>
                          ))}
                        </p>
                      )}
                    </article>
                  ))}
                </div>

                <div className="deep-dive-side-column">
                  <section className="deep-dive-panel deep-dive-panel-accent">
                    <h2>Build Principles</h2>
                    <div className="deep-dive-points">
                      {page.buildPrinciples.map((item) => (
                        <div key={item} className="deep-dive-point">
                          <span className="deep-dive-point-icon">+</span>
                          <p>{item}</p>
                        </div>
                      ))}
                    </div>
                  </section>

                  <section className="deep-dive-panel">
                    <h2>How I Work</h2>
                    <div className="deep-dive-process-steps">
                      {page.processSteps.map((step, index) => (
                        <div key={step.title} className="deep-dive-process-step">
                          <span className="deep-dive-process-index">{index + 1}</span>
                          <div>
                            <h3>{step.title}</h3>
                            <p>{step.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>

                  <section className="deep-dive-panel">
                    <h2>What You Can Expect</h2>
                    <ul className="deep-dive-checklist">
                      {page.expectations.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </section>
                </div>
              </div>

              <section className="deep-dive-panel deep-dive-vision-panel">
                <h2>The Vision</h2>
                {page.vision.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}

                {Array.isArray(page.relatedLinks) && page.relatedLinks.length > 0 && (
                  <div className="deep-dive-related-links">
                    {page.relatedLinks.map((link) => (
                      <Link key={link.to} to={link.to} className="deep-dive-related-link">
                        {link.label}
                      </Link>
                    ))}
                  </div>
                )}

                <p className="deep-dive-closing-cta">{page.closingCta}</p>
                <div className="deep-dive-actions">
                  <Link to="/#contact" className="btn-primary">Talk About Your Project</Link>
                </div>
              </section>
            </div>
          </motion.div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default DeepDivePage;